/**
 * EventsFallbackWithChecklist - Events fallback display with transfer checkboxes.
 *
 * Used inside entity change cards when change_type is UPDATED but no field-level
 * diffs are available. Shows each unique event code with a blue dot and an
 * optional transfer checkbox.
 */

import type { DomainEventSummarySchema } from '@/services/api/generated/crossMatterChangesService';

import { formatEventCode } from '../utils/formatting';
import type { TransferChecklist } from '../utils/useTransferChecklist';
import { TransferCheckbox } from './TransferCheckbox';

interface EventsFallbackWithChecklistProps {
  events: DomainEventSummarySchema[];
  checklist: TransferChecklist;
}

export const EventsFallbackWithChecklist: React.FC<
  EventsFallbackWithChecklistProps
> = ({ events, checklist }) => {
  if (events.length === 0) return null;

  const uniqueCodes = [...new Set(events.map((e) => e.event_code))];

  return (
    <div className="space-y-1">
      {uniqueCodes.map((code) => (
        <div key={code} className="flex items-center gap-2">
          <p className="flex items-center gap-1.5 text-xs text-gray-900 px-1 flex-1">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
            {formatEventCode(code)}
            {' \u2014 '}
            field-level diffs unavailable
          </p>
          {checklist.showCheckboxes && (
            <TransferCheckbox
              checked={checklist.isChecked(`event.${code}`)}
              onToggle={() => checklist.toggle(`event.${code}`)}
            />
          )}
        </div>
      ))}
    </div>
  );
};
